// Estilo editable del texto de la Portada (Home). Permite cambiar el color de
// letra, la intensidad de la sombra y la tipografía sin tocar código. Se guarda
// en company_settings y se expone al público en company_settings_public.

export type HeroTextShadow = 'none' | 'soft' | 'medium' | 'strong';
export type HeroTextFont = 'brand' | 'elegant' | 'modern' | 'rounded' | 'script';
// Intensidad del oscurecido (overlay) que se pone sobre el fondo de la portada
// para reforzar el contraste del texto, sobre todo cuando hay un video claro.
export type HeroOverlayIntensity = 'none' | 'light' | 'medium' | 'strong';
// Tamaño del título (portada) y de los títulos de sección.
export type HeroTitleSize = 'small' | 'medium' | 'large' | 'xlarge';
export type SectionTitleSize = 'small' | 'medium' | 'large' | 'xlarge';
// Grosor tipográfico (peso de la letra).
export type TextWeight = 'normal' | 'semibold' | 'bold' | 'extrabold' | 'black';

export interface HeroFontOption {
  value: HeroTextFont;
  label: string;
  fontFamily: string;
}

// Opciones de tipografía disponibles para el título de la portada.
export const HERO_FONT_OPTIONS: HeroFontOption[] = [
  { value: 'brand', label: 'Marca (Jakarta Sans)', fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif" },
  { value: 'elegant', label: 'Elegante (Playfair Display)', fontFamily: "'Playfair Display', Georgia, serif" },
  { value: 'modern', label: 'Moderna (Poppins)', fontFamily: "'Poppins', 'Inter', sans-serif" },
  { value: 'rounded', label: 'Redondeada (Baloo 2)', fontFamily: "'Baloo 2', 'Inter', sans-serif" },
  { value: 'script', label: 'Manuscrita (Pacifico)', fontFamily: "'Pacifico', cursive" },
];

export interface HeroShadowOption {
  value: HeroTextShadow;
  label: string;
}

// Intensidad de la sombra para que el texto resalte sobre fotos y videos claros.
export const HERO_SHADOW_OPTIONS: HeroShadowOption[] = [
  { value: 'none', label: 'Sin sombra' },
  { value: 'soft', label: 'Suave' },
  { value: 'medium', label: 'Media' },
  { value: 'strong', label: 'Fuerte' },
];

export interface ShadowColorPreset {
  value: string;
  label: string;
}

// Colores de la sombra del texto. Además del negro clásico, se incluyen tonos
// neón que hacen que la letra "brille" y resalte sobre fotos y videos claros.
export const HERO_SHADOW_COLOR_PRESETS: ShadowColorPreset[] = [
  { value: '#000000', label: 'Negro' },
  { value: '#00F0FF', label: 'Neón cian' },
  { value: '#39FF14', label: 'Neón verde' },
  { value: '#FF1FD1', label: 'Neón magenta' },
  { value: '#FFE600', label: 'Neón amarillo' },
  { value: '#FF6A00', label: 'Neón naranja' },
  { value: '#B026FF', label: 'Neón violeta' },
  { value: '#FFFFFF', label: 'Blanco' },
];

// Color por defecto de la sombra (negro, el look clásico y más legible).
export const DEFAULT_SHADOW_COLOR = '#000000';

export interface HeroColorPreset {
  value: string;
  label: string;
}

// Colores sugeridos para el texto de la portada (además del selector libre).
export const HERO_COLOR_PRESETS: HeroColorPreset[] = [
  { value: '#FFFFFF', label: 'Blanco' },
  { value: '#FDF6E3', label: 'Crema' },
  { value: '#111111', label: 'Negro' },
  { value: '#F5A623', label: 'Dorado' },
  { value: '#E85D9E', label: 'Rosa' },
];

// Colores sugeridos para los títulos de sección (fondos claros de la tienda).
export const SECTION_COLOR_PRESETS: HeroColorPreset[] = [
  { value: '#111827', label: 'Tinta' },
  { value: '#374151', label: 'Grafito' },
  { value: '#7C2D12', label: 'Cacao' },
  { value: '#9A3412', label: 'Terracota' },
  { value: '#FFFFFF', label: 'Blanco' },
];

export const DEFAULT_HERO_TEXT_COLOR = '#FFFFFF';
export const DEFAULT_HERO_TEXT_SHADOW: HeroTextShadow = 'soft';
export const DEFAULT_HERO_TEXT_FONT: HeroTextFont = 'brand';

function isFont(value: unknown): value is HeroTextFont {
  return HERO_FONT_OPTIONS.some((o) => o.value === value);
}

function isShadow(value: unknown): value is HeroTextShadow {
  return HERO_SHADOW_OPTIONS.some((o) => o.value === value);
}

// Normaliza la tipografía guardada (cualquier valor inválido cae a "Marca").
export function normalizeHeroFont(value: unknown): HeroTextFont {
  return isFont(value) ? value : DEFAULT_HERO_TEXT_FONT;
}

// Normaliza la intensidad de sombra guardada.
export function normalizeHeroShadow(value: unknown): HeroTextShadow {
  return isShadow(value) ? value : DEFAULT_HERO_TEXT_SHADOW;
}

// Valida un color hexadecimal (#RGB o #RRGGBB). Devuelve null si no es válido.
export function normalizeHeroColor(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed) || /^#[0-9a-fA-F]{3}$/.test(trimmed)) {
    return trimmed.toUpperCase();
  }
  return null;
}

// Normaliza el color de la sombra (cualquier valor inválido cae al negro).
export function normalizeShadowColor(value: unknown): string {
  return normalizeHeroColor(value) ?? DEFAULT_SHADOW_COLOR;
}

// Convierte un hex (#RGB o #RRGGBB) a rgba con el alfa indicado.
function hexToRgba(hex: string, alpha: number): string {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Resuelve la familia tipográfica CSS a partir de la opción guardada.
export function getHeroFontFamily(value: unknown): string {
  const key = normalizeHeroFont(value);
  const option = HERO_FONT_OPTIONS.find((o) => o.value === key);
  return option ? option.fontFamily : HERO_FONT_OPTIONS[0].fontFamily;
}

// Resuelve el text-shadow CSS a partir de la intensidad y el color guardados.
// Si no se pasa color, se usa negro (el look clásico). Los colores neón crean
// un halo que hace resaltar la letra sobre fondos claros.
export function getHeroTextShadow(value: unknown, color?: unknown): string {
  const c = normalizeShadowColor(color);
  switch (normalizeHeroShadow(value)) {
    case 'none':
      return 'none';
    case 'medium':
      return `0 2px 14px ${hexToRgba(c, 0.7)}, 0 1px 4px ${hexToRgba(c, 0.6)}`;
    case 'strong':
      return `0 2px 18px ${hexToRgba(c, 0.9)}, 0 2px 6px ${hexToRgba(c, 0.75)}, 0 0 36px ${hexToRgba(c, 0.6)}`;
    case 'soft':
    default:
      return `0 1px 10px ${hexToRgba(c, 0.55)}, 0 1px 2px ${hexToRgba(c, 0.5)}`;
  }
}

/* ------------------------------------------------------------------ */
/* Oscurecido (overlay) del fondo de la portada                        */
/* ------------------------------------------------------------------ */

export interface HeroOverlayOption {
  value: HeroOverlayIntensity;
  label: string;
  className: string;
}

// Intensidad del oscurecido sobre foto/video de la portada. La clase se aplica
// como capa absoluta encima del fondo y debajo del texto.
export const HERO_OVERLAY_OPTIONS: HeroOverlayOption[] = [
  { value: 'none', label: 'Sin oscurecer', className: '' },
  {
    value: 'light',
    label: 'Suave',
    className: 'bg-gradient-to-b from-black/25 via-black/10 to-black/25',
  },
  {
    value: 'medium',
    label: 'Media',
    className: 'bg-gradient-to-b from-black/50 via-black/30 to-black/50',
  },
  {
    value: 'strong',
    label: 'Fuerte',
    className: 'bg-gradient-to-b from-black/75 via-black/55 to-black/75',
  },
];

export const DEFAULT_HERO_OVERLAY_INTENSITY: HeroOverlayIntensity = 'medium';

/* ------------------------------------------------------------------ */
/* Tamaño y grosor del título                                        */
/* ------------------------------------------------------------------ */

export interface SizeOption<T extends string> {
  value: T;
  label: string;
  className: string;
}

export const HERO_TITLE_SIZE_OPTIONS: SizeOption<HeroTitleSize>[] = [
  { value: 'small', label: 'Pequeño', className: 'text-2xl sm:text-3xl md:text-4xl' },
  { value: 'medium', label: 'Mediano', className: 'text-3xl sm:text-4xl md:text-5xl' },
  { value: 'large', label: 'Grande', className: 'text-3xl sm:text-4xl md:text-5xl lg:text-6xl' },
  { value: 'xlarge', label: 'Muy grande', className: 'text-4xl sm:text-5xl md:text-6xl lg:text-7xl' },
];

export const SECTION_TITLE_SIZE_OPTIONS: SizeOption<SectionTitleSize>[] = [
  { value: 'small', label: 'Pequeño', className: 'text-xl md:text-2xl' },
  { value: 'medium', label: 'Mediano', className: 'text-2xl md:text-3xl' },
  { value: 'large', label: 'Grande', className: 'text-3xl md:text-4xl' },
  { value: 'xlarge', label: 'Muy grande', className: 'text-4xl md:text-5xl' },
];

export const DEFAULT_HERO_TITLE_SIZE: HeroTitleSize = 'large';
export const DEFAULT_SECTION_TITLE_SIZE: SectionTitleSize = 'medium';

export interface WeightOption {
  value: TextWeight;
  label: string;
  className: string;
}

export const TEXT_WEIGHT_OPTIONS: WeightOption[] = [
  { value: 'normal', label: 'Normal', className: 'font-normal' },
  { value: 'semibold', label: 'Seminegrita', className: 'font-semibold' },
  { value: 'bold', label: 'Negrita', className: 'font-bold' },
  { value: 'extrabold', label: 'Súper negrita', className: 'font-extrabold' },
  { value: 'black', label: 'Extra gruesa', className: 'font-black' },
];

export const DEFAULT_HERO_TITLE_WEIGHT: TextWeight = 'extrabold';
export const DEFAULT_SECTION_TITLE_WEIGHT: TextWeight = 'bold';

/* ------------------------------------------------------------------ */
/* Estilo por defecto de los títulos de sección (Referencias/Propósito) */
/* ------------------------------------------------------------------ */

export const DEFAULT_SECTION_TEXT_COLOR = '#111827';
export const DEFAULT_SECTION_TEXT_SHADOW: HeroTextShadow = 'none';
export const DEFAULT_SECTION_TEXT_FONT: HeroTextFont = 'brand';

/* ------------------------------------------------------------------ */
/* Normalizadores                                                      */
/* ------------------------------------------------------------------ */

function isOverlay(value: unknown): value is HeroOverlayIntensity {
  return HERO_OVERLAY_OPTIONS.some((o) => o.value === value);
}

function isHeroSize(value: unknown): value is HeroTitleSize {
  return HERO_TITLE_SIZE_OPTIONS.some((o) => o.value === value);
}

function isSectionSize(value: unknown): value is SectionTitleSize {
  return SECTION_TITLE_SIZE_OPTIONS.some((o) => o.value === value);
}

function isWeight(value: unknown): value is TextWeight {
  return TEXT_WEIGHT_OPTIONS.some((o) => o.value === value);
}

export function normalizeHeroOverlay(value: unknown): HeroOverlayIntensity {
  return isOverlay(value) ? value : DEFAULT_HERO_OVERLAY_INTENSITY;
}

export function normalizeHeroTitleSize(value: unknown): HeroTitleSize {
  return isHeroSize(value) ? value : DEFAULT_HERO_TITLE_SIZE;
}

export function normalizeSectionTitleSize(value: unknown): SectionTitleSize {
  return isSectionSize(value) ? value : DEFAULT_SECTION_TITLE_SIZE;
}

export function normalizeTextWeight(value: unknown): TextWeight {
  return isWeight(value) ? value : DEFAULT_HERO_TITLE_WEIGHT;
}

// Normaliza el estilo de los títulos de sección, con sus propios valores por
// defecto (sombra "sin sombra" y tipografía de marca).
export function normalizeSectionShadow(value: unknown): HeroTextShadow {
  return isShadow(value) ? value : DEFAULT_SECTION_TEXT_SHADOW;
}

export function normalizeSectionFont(value: unknown): HeroTextFont {
  return isFont(value) ? value : DEFAULT_SECTION_TEXT_FONT;
}

/* ------------------------------------------------------------------ */
/* Resolvers (valor guardado -> clase / estilo CSS)                    */
/* ------------------------------------------------------------------ */

export function getHeroOverlayClass(value: unknown): string {
  const key = normalizeHeroOverlay(value);
  const option = HERO_OVERLAY_OPTIONS.find((o) => o.value === key);
  return option ? option.className : '';
}

export function getHeroTitleSizeClass(value: unknown): string {
  const key = normalizeHeroTitleSize(value);
  const option = HERO_TITLE_SIZE_OPTIONS.find((o) => o.value === key);
  return option ? option.className : HERO_TITLE_SIZE_OPTIONS[2].className;
}

export function getSectionTitleSizeClass(value: unknown): string {
  const key = normalizeSectionTitleSize(value);
  const option = SECTION_TITLE_SIZE_OPTIONS.find((o) => o.value === key);
  return option ? option.className : SECTION_TITLE_SIZE_OPTIONS[1].className;
}

export function getTextWeightClass(value: unknown): string {
  const key = normalizeTextWeight(value);
  const option = TEXT_WEIGHT_OPTIONS.find((o) => o.value === key);
  return option ? option.className : 'font-bold';
}